/**
 * The clefs OSMD laid out on a repertoire line, read from VexFlow's staves.
 *
 * The repertoire scene skips OSMD's clef glyphs and draws its own clef images, so it
 * has to know which clef each staff line opens with (a left hand can start in treble
 * clef) and where each clef change at a barline sits.
 */

/** A clef VexFlow laid out on a stave (a stave modifier of category 'clefs'). */
interface VexFlowClefModifier {
  getCategory(): string;
  getPosition(): number;
  getX(): number;
  getStyle?: () => { fillStyle?: unknown } | undefined;
  type: string;
}

const isVexFlowClefModifier = (value: unknown): value is VexFlowClefModifier =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as Record<string, unknown>).getCategory === 'function' &&
  (value as { getCategory: () => unknown }).getCategory() === 'clefs' &&
  typeof (value as Record<string, unknown>).getX === 'function' &&
  typeof (value as Record<string, unknown>).getPosition === 'function' &&
  typeof (value as Record<string, unknown>).type === 'string';

/**
 * OSMD adds a clef change to every staff of a system so the bars stay aligned, and
 * hides it (a fully transparent fill such as #12345600) on staves whose clef does not
 * change.
 */
const isHiddenClef = (clef: VexFlowClefModifier): boolean => {
  const fillStyle = clef.getStyle?.()?.fillStyle;
  return typeof fillStyle === 'string' && /^#[0-9a-f]{6}00$/i.test(fillStyle);
};

/** VexFlow's StaveModifier position for a modifier at the start of a bar. */
const STAVE_MODIFIER_BEGIN = 5;

export type StaffLineClef = {
  /** VexFlow's clef name: 'treble', 'bass', 'alto' and so on. */
  type: string;
  /** Where VexFlow drew it, in the phrase's drawing coordinates. */
  x: number;
  /** The clef the staff line opens with, as opposed to a change. */
  atLineStart: boolean;
};

/**
 * The visible clefs VexFlow laid out on one staff line's bars (OSMD's
 * `StaffLine.Measures`), in order: the one at the start of the line, and every clef
 * change at a barline (at the end of a bar or the start of the next). Read after the
 * line is drawn.
 */
export const readStaffLineClefs = (measures: readonly unknown[]): StaffLineClef[] => {
  if (!Array.isArray(measures))
    throw new TypeError('readStaffLineClefs: measures must be an array');
  return measures.flatMap((measure, measureIndex) => {
    const stave = (
      measure as { getVFStave?: () => { getModifiers?: () => unknown[] } | null } | null
    )?.getVFStave?.();
    return (stave?.getModifiers?.() ?? [])
      .filter(isVexFlowClefModifier)
      .filter((clef) => !isHiddenClef(clef))
      .map((clef) => ({
        type: clef.type,
        x: clef.getX(),
        atLineStart: measureIndex === 0 && clef.getPosition() === STAVE_MODIFIER_BEGIN,
      }));
  });
};
